import { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ScrollReveal from '@/components/animations/ScrollReveal';
import { useRTLArrow } from '@/hooks/useRTL';
import { useContent } from '@/hooks/useContent';

gsap.registerPlugin(ScrollTrigger);

const VirtualTour = () => {
  const { t } = useContent();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [currentImage, setCurrentImage] = useState(0);
  const { getChevron } = useRTLArrow();
  const PrevChevron = getChevron('left');
  const NextChevron = getChevron('right');

  const galleryImages = [
    { id: 1, titleKey: 'virtualTour.reception.title', descriptionKey: 'virtualTour.reception.description' },
    { id: 2, titleKey: 'virtualTour.laboratory.title', descriptionKey: 'virtualTour.laboratory.description' },
    { id: 3, titleKey: 'virtualTour.sampleCollection.title', descriptionKey: 'virtualTour.sampleCollection.description' },
    { id: 4, titleKey: 'virtualTour.analysisRoom.title', descriptionKey: 'virtualTour.analysisRoom.description' },
    { id: 5, titleKey: 'virtualTour.waitingArea.title', descriptionKey: 'virtualTour.waitingArea.description' },
    { id: 6, titleKey: 'virtualTour.qualityControl.title', descriptionKey: 'virtualTour.qualityControl.description' },
  ];

  useEffect(() => {
    document.title = `${t('common.virtualTour')} - ElhamyLabs`;
  }, [t]);

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % galleryImages.length);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-brand-purple-dark via-brand-purple-base to-brand-purple-light dark:from-dark-primary dark:via-dark-secondary">
        <div className="container mx-auto px-4 text-center">
          <ScrollReveal>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white">
              {t('virtualTour.title')}
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              {t('virtualTour.subtitle')}
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Gallery Section */}
      <section ref={sectionRef} className="py-20 bg-light-primary dark:bg-dark-primary">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="relative mb-8">
              <div className="aspect-video bg-gradient-to-br from-brand-purple-base to-brand-orange-base rounded-xl overflow-hidden">
                <div className="w-full h-full flex items-center justify-center text-white text-4xl font-bold">
                  {t(galleryImages[currentImage].titleKey)}
                </div>
              </div>
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 transition-colors flex items-center justify-center"
              >
                <PrevChevron size={24} className="text-white" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 w-12 h-12 rounded-full bg-white/20 backdrop-blur-md hover:bg-white/30 transition-colors flex items-center justify-center"
              >
                <NextChevron size={24} className="text-white" />
              </button>
            </div>
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-2 text-light-text dark:text-dark-text">
                {t(galleryImages[currentImage].titleKey)}
              </h3>
              <p className="text-light-text-secondary dark:text-dark-text-secondary">
                {t(galleryImages[currentImage].descriptionKey)}
              </p>
            </div>
          </div>

          {/* Thumbnail Grid */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
            {galleryImages.map((image, index) => (
              <ScrollReveal key={image.id} delay={index * 0.05}>
                <button
                  onClick={() => setCurrentImage(index)}
                  className={`aspect-square rounded-lg overflow-hidden transition-all duration-300 ${
                    currentImage === index
                      ? 'ring-4 ring-brand-orange-base scale-105'
                      : 'opacity-70 hover:opacity-100 hover:scale-105'
                  }`}
                >
                  <div className="w-full h-full bg-gradient-to-br from-brand-purple-base to-brand-orange-base flex items-center justify-center text-white text-sm font-semibold">
                    {t(image.titleKey)}
                  </div>
                </button>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default VirtualTour;

